'use client'
import React from "react";
import classNames from "classnames";
import { ChevronDownIcon } from "@/migrated-widgets/table-map/components/icons/ChevronDownIcon";

import { TableSideProps } from "./types";
import { useAudienceVisitorsTableMapState } from "./AudienceVisitorsTableMapState";
import { SubCollection } from "@/migrated-widgets/table-map/services/MapDataLayer/types/Engage.types";
import { TableFooter } from "./TableFooter";
import { audienceRecordUniqueId } from "./constants";
import { IAudienceGroupItem } from "./hooks/useAudienceGroups";
import { formatValue } from "@/migrated-widgets/table-map/utils/random";

const TableSide: React.FC<TableSideProps> = () => {
    const {
        setAudiencesIds, audiencesIds, zipCodes: activeZipCodes, audienceGroups,
        groupIds, makeIds, modelIds,
        setGroupIds, setMakeIds, setModelIds, currentlyViewing
    } = useAudienceVisitorsTableMapState();
    const [isExpanded, setIsExpanded] = React.useState(new Map<string, boolean>());

    const toggle = (setter: React.Dispatch<React.SetStateAction<any[]>>, id: any) => {
        setter(prev => {
            if (prev.includes(id)) return prev.filter((el) => el !== id)
            return [...prev, id]
        });
    }

    // Group / audience / make / model are mutually exclusive selection levels in this
    // table - selecting a row at one level should switch straight to it, not require
    // deselecting whatever's active at another level first.
    const selectAudience = (audience: SubCollection) => {
        if (activeZipCodes.length > 0) return;

        toggle(setAudiencesIds, audienceRecordUniqueId(audience));
        setGroupIds([]);
        setMakeIds([]);
        setModelIds([]);
    }

    const selectGroup = (id: number) => {
        toggle(setGroupIds, id);
        setAudiencesIds([]);
        setMakeIds([]);
        setModelIds([]);
    }

    const selectMake = (id: string) => {
        toggle(setMakeIds, id);
        setGroupIds([]);
        setAudiencesIds([]);
        setModelIds([]);
    }

    const selectModel = (id: string) => {
        toggle(setModelIds, id);
        setGroupIds([]);
        setAudiencesIds([]);
        setMakeIds([]);
    }

    const matchZipCodes = (group: IAudienceGroupItem) => {
        const _zipcodes = group.zipCodes ?? [];
        return activeZipCodes.length > 0 && _zipcodes.some(zipcode => activeZipCodes.includes(zipcode));
    }

    const isActiveGroup = (group: IAudienceGroupItem) => {
        if (activeZipCodes.length) return matchZipCodes(group);
        return groupIds.includes(group.id);
    }

    const isActiveAudience = (audience: IAudienceGroupItem) => {
        console.log(audiencesIds)
        const selected = audiencesIds.includes(audienceRecordUniqueId(audience))
        if (groupIds.length > 0) return false;
        if (activeZipCodes.length) return matchZipCodes(audience);

        return selected;
    }


    const isActiveMake = (make: IAudienceGroupItem) => {
        if (groupIds.length > 0) return false;
        if (audiencesIds.length > 0) return false;
        if (activeZipCodes.length) return matchZipCodes(make);

        return makeIds.includes(`${make.data.groupId}_${make.data.audienceVisitorReportMakeId}`);

    }

    const isActiveModel = (model: IAudienceGroupItem) => {
        if (groupIds.length > 0) return false;
        if (audiencesIds.length > 0) return false;
        if (makeIds.length > 0) return false;
        if (activeZipCodes.length) return matchZipCodes(model);

        return modelIds.includes(`${model.data.groupId}_${model.data.audienceVisitorReportMakeId}_${model.data.audienceVisitorReportModelId}`);
    }

    const handleExpand = (e: React.MouseEvent, key: string) => {
        e.stopPropagation();
        isExpanded.set(key, !isExpanded.get(key));
        setIsExpanded(new Map(isExpanded));
    }

    const getExpanded = (key: string) => {
        if (activeZipCodes.length) return true;
        return isExpanded.get(key);
    }

    return (
        <>
            <div className={classNames('sticky', { 'top-[2.975rem]': currentlyViewing || activeZipCodes.length > 0, 'top-0': !currentlyViewing && !activeZipCodes.length }, 'z-10', 'flex', 'justify-between', 'items-center', 'w-full', 'px-10', 'py-3', 'border-b', 'border-b-table-map-lightGray', 'gap-2', 'bg-[#f9fafa]', 'font-bold')}>
                <div className={classNames('flex', 'items-center', 'gap-1', 'text-sm', 'tracking-[-0.15]', 'text-left', 'text-brand-black', 'basis-13/20')}>
                    Audience
                </div>
                <div className={classNames('flex', 'items-center', 'gap-1', 'text-sm', 'tracking-[-0.15]', 'text-left', 'text-brand-black', '!basis-7/20', 'justify-center')}>
                    Size
                </div>
            </div>
            <div className={classNames('flex-1')}>
                {
                    audienceGroups.map((audienceGroup) => (
                        <React.Fragment key={audienceGroup.id}>
                            <div
                                className={classNames('flex', 'justify-between', 'items-center', 'w-full', 'px-10', 'py-3', 'border-b', 'border-b-table-map-lightGray', 'gap-2', 'hover:backdrop-brightness-95', {
                                    'cursor-pointer': !activeZipCodes.length,
                                    'bg-table-map-teal': isActiveGroup(audienceGroup)
                                })}
                                onClick={() => selectGroup(audienceGroup.id)}
                            >
                                <div className={classNames('flex', 'items-center', 'gap-1', 'text-sm', 'tracking-[-0.15]', 'text-left', 'text-brand-black', 'basis-13/20')}>
                                    {audienceGroup.expansible && (
                                        <ChevronDownIcon
                                            className={classNames('w-4 mr-2', { '-rotate-90': !getExpanded(String(audienceGroup.id)) })}
                                            onClick={(e: React.MouseEvent) => handleExpand(e, String(audienceGroup.id))}
                                        />
                                    )}
                                    {audienceGroup.name}
                                </div>
                                <div className={classNames('flex', 'items-center', 'gap-1', 'text-sm', 'tracking-[-0.15]', 'text-left', 'text-brand-black', '!basis-7/20', 'justify-center')}>
                                    {formatValue(audienceGroup.visitorsCount)}
                                </div>
                            </div>
                            {getExpanded(String(audienceGroup.id)) && audienceGroup.children?.map((make) => (
                                <React.Fragment key={make.id}>
                                    <div
                                        className={classNames('flex', 'justify-between', 'items-center', 'w-full', 'px-10', 'py-3', 'border-b', 'border-b-table-map-lightGray', 'gap-2', 'hover:backdrop-brightness-95', 'pl-14', 'bg-brand-table-map-lightGray/20', {
                                            'cursor-pointer': !activeZipCodes.length,
                                            'bg-table-map-teal': isActiveMake(make)
                                        })}
                                        onClick={() => selectMake(`${make.data.groupId}_${make.data.audienceVisitorReportMakeId}`)}
                                    >
                                        <div className={classNames('flex', 'items-center', 'gap-1', 'text-sm', 'tracking-[-0.15]', 'text-left', 'text-brand-black', 'basis-13/20')}>
                                            {make?.children && (
                                                <ChevronDownIcon
                                                    className={classNames('w-4 mr-2', { '-rotate-90': !getExpanded(String(make.id)) })}
                                                    onClick={(e: React.MouseEvent) => handleExpand(e, String(make.id))}
                                                />
                                            )}
                                            {make.name}
                                        </div>
                                        <div className={classNames('flex', 'items-center', 'gap-1', 'text-sm', 'tracking-[-0.15]', 'text-left', 'text-brand-black', '!basis-7/20', 'justify-center')}>
                                            {formatValue(make.visitorsCount)}
                                        </div>
                                    </div>
                                    {getExpanded(String(make.id)) && make.children?.map((model) => (
                                        <div
                                            key={model.id}
                                            className={classNames('flex', 'justify-between', 'items-center', 'w-full', 'px-10', 'py-3', 'border-b', 'border-b-table-map-lightGray', 'gap-2', 'hover:backdrop-brightness-95', 'pl-20', 'bg-brand-table-map-lightGray/50', {
                                                'cursor-pointer': !activeZipCodes.length,
                                                'bg-table-map-teal': isActiveModel(model)
                                            })}
                                            onClick={() => selectModel(`${model.data.groupId}_${model.data.audienceVisitorReportMakeId}_${model.data.audienceVisitorReportModelId}`)}
                                        >
                                            <div className={classNames('flex', 'items-center', 'gap-1', 'text-sm', 'tracking-[-0.15]', 'text-left', 'text-brand-black', 'basis-13/20')}>
                                                {model.name}
                                            </div>
                                            <div className={classNames('flex', 'items-center', 'gap-1', 'text-sm', 'tracking-[-0.15]', 'text-left', 'text-brand-black', '!basis-7/20', 'justify-center')}>
                                                {formatValue(model.visitorsCount)}
                                            </div>
                                        </div>
                                    ))}
                                </React.Fragment>
                            ))}
                        </React.Fragment>
                    ))
                }
                <TableFooter />
            </div>
        </>
    )
}
export default TableSide;